import { useMemo } from 'react'
import { Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import styled from 'styled-components'
import { TFunction } from 'i18next'
import { theme } from '../theme/theme'
import { BodySmallMedium } from './styles/Typography'
import { CaseResponse } from '../api/ester-b2b-api/private/case/types'
import { CaseStatus } from '@estercare/ester-shared'
import { Tag } from './Tag'
import { formatRelativeTime } from '../utils/dates'
import { useTranslation } from 'react-i18next'
import { useSelectFirstName } from '../state/selectors'
import { getSenderTitle, isCaseUnread } from '../utils/caseMessages'

type Props = {
  cases: CaseResponse[]
  onCaseClick: (caseId: string) => void
  isMobile: boolean
}

const getCaseTag = (caseItem: CaseResponse, t: TFunction): JSX.Element | null => {
  if (caseItem.status === CaseStatus.CLOSED) {
    return <Tag text={t('employee.messages.closedCase')} color={theme.color.grey3} />
  }
  return null
}

export const CaseTable = ({ cases, onCaseClick, isMobile }: Props) => {
  const { t } = useTranslation()
  const firstName = useSelectFirstName()
  const TEXT_CUTOFF_MOBILE = 20
  const TEXT_CUTOFF_DESKTOP = 50

  const columns = useMemo(
    () => [
      {
        key: 'msgPreview',
        header: t('employee.messages.tableHeaderId'),
        render: (caseItem: CaseResponse) => {
          const { lastMessage } = caseItem
          if (!lastMessage) return '-'
          const { sender, message } = lastMessage
          const isUnread = isCaseUnread(caseItem)
          const textCutoff = isMobile ? TEXT_CUTOFF_MOBILE : TEXT_CUTOFF_DESKTOP

          return (
            <MsgPreviewContainer>
              <MsgText>
                <SenderRow>
                  {isUnread && <UnreadDot />}
                  <Sender>
                    {(sender && getSenderTitle(sender, firstName)) || t('employee.messages.fallbackSender')}
                  </Sender>
                </SenderRow>
                <GradientTextContainer>
                  {message ? `${message.slice(0, textCutoff).trim()}...` : '...'}
                </GradientTextContainer>
              </MsgText>
            </MsgPreviewContainer>
          )
        },
        width: '40%',
      },
      {
        key: 'status',
        header: '',
        render: (caseItem: CaseResponse) => getCaseTag(caseItem, t),
        width: isMobile ? '35%' : 'auto',
      },
      {
        key: 'lastMessageDate',
        header: t('employee.messages.tableHeaderLastMessageDate'),
        render: (caseItem: CaseResponse) =>
          caseItem.lastMessage?.createdAt && formatRelativeTime(new Date(caseItem.lastMessage.createdAt), t),
      },
    ],
    [t, isMobile, firstName]
  )

  if (cases.length === 0) return null

  return (
    <StyledTableContainer>
      <MuiTable>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <StyledTableCell key={column.key} $width={column.width}>
                <BodySmallMedium>{column.header}</BodySmallMedium>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {cases.map((caseItem) => (
            <ClickableTableRow
              key={caseItem.id}
              onClick={() => onCaseClick(caseItem.id)}
              $isUnread={isCaseUnread(caseItem)}
            >
              {columns.map((column) => (
                <StyledTableCell key={column.key} align="left" $width={column.width}>
                  {column.render(caseItem)}
                </StyledTableCell>
              ))}
            </ClickableTableRow>
          ))}
        </TableBody>
      </MuiTable>
    </StyledTableContainer>
  )
}

const MsgPreviewContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.xsmall}px;
`
const MsgText = styled.div`
  display: flex;
  flex-direction: column;
`

const SenderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
`

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background-color: ${theme.color.plumMid};
`

const Sender = styled(BodySmallMedium)``

const GradientTextContainer = styled.div`
  background: linear-gradient(20deg, black, ${theme.color.beigeLight});
  white-space: nowrap;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`

const StyledTableContainer = styled(TableContainer)`
  .MuiTable-root {
    table-layout: fixed;
    width: 100%;
  }
`

const ClickableTableRow = styled(TableRow)<{ $isUnread: boolean }>`
  cursor: pointer;

  ${({ $isUnread }) =>
    $isUnread &&
    `
    background-color: rgba(121, 79, 183, 0.08);
    td, td * { font-weight: 500; }
  `}

  &:hover {
    background-color: ${theme.color.beigeDark};
  }
`

const StyledTableCell = styled(TableCell)<{ $width?: string }>`
  padding: ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.large}px;
  ${({ $width }) =>
    $width &&
    `
    width: ${$width};
    max-width: ${$width};
    min-width: ${$width};
  `}
`
